import React, { useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  CssBaseline,
  IconButton,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';
// import AssignmentIcon from '@mui/icons-material/Assignment';
// import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
// import DescriptionIcon from '@mui/icons-material/Description';
import ContactMailIcon from '@mui/icons-material/ContactMail';
import DescriptionIcon from '@mui/icons-material/Description';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import StorefrontIcon from '@mui/icons-material/Storefront';
// import HistoryIcon from '@mui/icons-material/History';
// import EventIcon from '@mui/icons-material/Event';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PeopleIcon from '@mui/icons-material/People';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';

// Pages
import Dashboard from './pages/Dashboard';
import MyProgress from './pages/MyProgress';
import MyOrders from './pages/MyOrders';
import MyQuotations from './pages/MyQuotations';
import MyEnquiry from './pages/MyEnquiry';
import Login from './pages/Login';
import EmployeeManagement from './pages/EmployeeManagement';
import GenerateQuotation from './pages/GenerateQuotation';
import PlantProposal from './pages/PlantProposal';
import IndiaMART from './pages/IndiaMART';
import IndiamartCoverage from './pages/IndiamartCoverage';

// Contexts & Utils
import { useAuth } from './contexts/AuthContext';
// import { isAuthenticated } from './services/authApi';

const drawerWidth = 260;
const collapsedDrawerWidth = 64;
// useRef

const menuItems = [
  { text: 'My Dashboard', icon: <HomeIcon />, path: '/' },
  { text: 'My Enquiry', icon: <ContactMailIcon />, path: '/my-enquiry' },
  { text: 'My Quotations', icon: <DescriptionIcon />, path: '/my-quotations' },
  { text: 'My Orders', icon: <DescriptionIcon />, path: '/my-orders' },
  { text: 'Plant Proposal', icon: <DescriptionIcon />, path: '/plant-proposal' },
  { text: 'IndiaMART Leads',    icon: <StorefrontIcon />,    path: '/indiamart',          superAdminOnly: true },
  { text: 'Syncing IndiaMART', icon: <CalendarMonthIcon />, path: '/indiamart-coverage', superAdminOnly: true },
];

// Protected Route Component
const ProtectedRoute = ({ children, superAdminOnly = false }) => {
  const { isAuthenticated: authStatus, isSuperAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const redirectAttemptedRef = useRef(false);

  useEffect(() => {
    // Wait for auth to finish loading
    if (loading) return;

    // Only redirect once
    if (redirectAttemptedRef.current) return;

    if (!authStatus) {
      redirectAttemptedRef.current = true;
      navigate('/login', { replace: true });
    } else if (superAdminOnly && !isSuperAdmin) {
      redirectAttemptedRef.current = true;
      navigate('/', { replace: true });
    }
  }, [authStatus, isSuperAdmin, superAdminOnly, loading, navigate]);

  // Show loading while auth is initializing
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Don't render children if not authenticated (redirect is in progress)
  if (!authStatus) return null;

  // Don't render children if superadmin-only and user is not superadmin
  if (superAdminOnly && !isSuperAdmin) return null;

  return children;
};

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { user, logout, isAdmin, isSuperAdmin, loading: authLoading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(isTablet); // Default collapsed on tablet
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  // Don't render main layout on login page
  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  // Show loading while auth is initializing (but not on login page)
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarCollapse = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    handleUserMenuClose();
    logout();
    // Navigation to login is handled by logout in AuthContext
    // But since we removed navigate from AuthContext, we need to do it here
    navigate('/login', { replace: true });
  };

  const handleMyAccount = () => {
    handleUserMenuClose();
    // TODO: Navigate to account page when implemented
    console.log('My Account clicked');
  };

  const handleEmployeeManagement = () => {
    handleUserMenuClose();
    navigate('/employee-management');
  };

  const currentDrawerWidth = sidebarCollapsed ? collapsedDrawerWidth : drawerWidth;

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return 'User';
    return user.full_name || user.user_name || user.employee_id || 'User';
  };

  const getUserRole = () => {
    if (!user) return '';
    if (isAdmin) return 'Admin';
    return user.designation || 'Employee';
  };

  // Don't render main layout on login page
  if (location.pathname === '/login') {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
      </Routes>
    );
  }

  // Show loading while auth is initializing (but not on login page)
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo/Company Section */}
      <Box
        sx={{
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: sidebarCollapsed ? 'center' : 'space-between',
          px: sidebarCollapsed ? 1 : 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        {!sidebarCollapsed && !isTablet && (
          <Typography variant="h6" sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'primary.main' }}>
            VACSPL ENQUIRY
          </Typography>
        )}
        {!isMobile && !isTablet && (
          <IconButton
            size="small"
            onClick={handleSidebarCollapse}
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          >
            {sidebarCollapsed ? <ChevronRightIcon fontSize="small" /> : <ChevronLeftIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', py: 1 }}>
        <List sx={{ px: 1 }}>
          {menuItems.filter((item) => !item.superAdminOnly || isSuperAdmin).map((item) => {
            const isSelected = location.pathname === item.path;
            return (
              <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={isSelected}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                  sx={{
                    minHeight: 40,
                    borderRadius: 2,
                    px: 1.5,
                    py: 1,
                    mx: 0.5,
                    transition: 'all 0.2s ease',
                    '&.Mui-selected': {
                      backgroundColor: 'secondary.main',
                      color: 'secondary.contrastText',
                      '&:hover': {
                        backgroundColor: 'secondary.light',
                      },
                      '& .MuiListItemIcon-root': {
                        color: 'secondary.contrastText',
                      },
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                      transform: 'translateX(2px)',
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? 0 : 40,
                      justifyContent: 'center',
                      color: isSelected ? 'secondary.contrastText' : 'text.secondary',
                      transition: 'color 0.2s ease',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.875rem',
                        fontWeight: isSelected ? 600 : 500,
                      }}
                    />
                  )}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>

      {/* User Profile Section */}
      {!sidebarCollapsed && !isTablet && user && (
        <Box
          sx={{
            p: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
              <AccountCircleIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8125rem' }} noWrap>
                {getUserDisplayName()}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                {getUserRole()}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <CssBaseline />
      
      {/* App Bar / Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${isTablet ? collapsedDrawerWidth : currentDrawerWidth}px)` },
          ml: { sm: `${isTablet ? collapsedDrawerWidth : currentDrawerWidth}px` },
          backgroundColor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          color: 'text.primary',
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar sx={{ 
          minHeight: { xs: '48px !important', sm: '56px !important' }, 
          px: { xs: 1, sm: 2, md: 3 },
          py: { xs: 0.5, sm: 0 }
        }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: { xs: 1, sm: 2 }, 
              display: { sm: 'none' },
              padding: { xs: '8px', sm: '12px' }
            }}
          >
            <MenuIcon fontSize={isMobile ? 'medium' : 'large'} />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              fontSize: { xs: '0.875rem', sm: '1rem' }, 
              fontWeight: 600, 
              flexGrow: 1,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {isMobile ? 'CRM' : menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          {isMobile && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontSize: '0.75rem', 
                fontWeight: 600, 
                flexGrow: 1,
                display: { xs: 'block', sm: 'none' }
              }}
            >
              {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            <IconButton
              onClick={handleUserMenuOpen}
              sx={{ 
                p: 0.5,
                '&:hover': { backgroundColor: 'action.hover' }
              }}
            >
              <Avatar sx={{ 
                width: { xs: 28, sm: 32 }, 
                height: { xs: 28, sm: 32 }, 
                bgcolor: 'primary.main',
                cursor: 'pointer'
              }}>
                <AccountCircleIcon fontSize="small" />
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleMyAccount}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                My Account
              </MenuItem>
              {isAdmin && (
                <MenuItem onClick={handleEmployeeManagement}>
                  <ListItemIcon>
                    <PeopleIcon fontSize="small" />
                  </ListItemIcon>
                  Add / Edit Employee
                </MenuItem>
              )}
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <LogoutIcon fontSize="small" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer / Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { sm: currentDrawerWidth },
          flexShrink: { sm: 0 },
        }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
        {/* Desktop/Tablet drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isTablet ? collapsedDrawerWidth : currentDrawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${isTablet ? collapsedDrawerWidth : currentDrawerWidth}px)` },
          backgroundColor: 'background.default',
          minHeight: '100vh',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: '56px !important' }} />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-progress"
            element={
              <ProtectedRoute>
                <MyProgress />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-orders"
            element={
              <ProtectedRoute>
                <MyOrders />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-quotations"
            element={
              <ProtectedRoute>
                <MyQuotations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-enquiry"
            element={
              <ProtectedRoute>
                <MyEnquiry />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plant-proposal"
            element={
              <ProtectedRoute>
                <PlantProposal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/generatequotation"
            element={
              <ProtectedRoute>
                <GenerateQuotation />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-management"
            element={
              <ProtectedRoute>
                <EmployeeManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/indiamart"
            element={
              <ProtectedRoute superAdminOnly>
                <IndiaMART />
              </ProtectedRoute>
            }
          />
          <Route
            path="/indiamart-coverage"
            element={
              <ProtectedRoute superAdminOnly>
                <IndiamartCoverage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-services-history"
            element={
              <ProtectedRoute>
                <Container maxWidth="xl" sx={{ py: 2 }}>
                  <Typography variant="h5" sx={{ mb: 2, fontSize: '1.25rem' }}>My Services History</Typography>
                  <Typography variant="body2" color="text.secondary">
                    This page is a placeholder and will be implemented in the future.
                  </Typography>
                </Container>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-demos"
            element={
              <ProtectedRoute>
                <Container maxWidth="xl" sx={{ py: 2 }}>
                  <Typography variant="h5" sx={{ mb: 2, fontSize: '1.25rem' }}>My Demos</Typography>
                  <Typography variant="body2" color="text.secondary">
                    This page is a placeholder and will be implemented in the future.
                  </Typography>
                </Container>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>
    </Box>
  );
}

export default App;
